import * as React from 'react'
import type { DialogProps } from '@radix-ui/react-dialog'
import { cn } from '@dialogueconsulting/sked-ui/lib/utils'
import type { PropsWithChildren } from 'react'
import { useEffect, useRef } from 'react'

import { CloseIcon } from 'Icons'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from 'ui/dialog'

const FALLBACK_DIALOG_TITLE = 'Dialog'

function hasDialogTitle(children: React.ReactNode): boolean {
  let foundTitle = false

  React.Children.forEach(children, (child) => {
    if (foundTitle || !React.isValidElement(child)) {
      return
    }

    if (child.props['data-slot'] === 'dialog-title') {
      foundTitle = true
      return
    }

    if ('children' in child.props) {
      foundTitle = hasDialogTitle(child.props.children)
    }
  })

  return foundTitle
}

export interface ModalProps extends DialogProps {
  trigger?: React.ReactNode // trigger element
  className?: string // for modal body
  children: React.ReactNode
}

function Modal({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  defaultOpen,
  trigger,
  className,
  children,
  ...props
}: PropsWithChildren<ModalProps>) {
  const closeBtnRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeBtnRef) {
        closeBtnRef.current?.click()
      }
    }
    window.addEventListener('keydown', handleEsc)
    return () => {
      window.removeEventListener('keydown', handleEsc)
    }
  }, [])

  const needsFallbackTitle = !hasDialogTitle(children)

  return (
    <Dialog {...props}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent
        showCloseButton={false}
        // w-auto/max-w-none + gap-0/p-4: consumers size the modal via their
        // content (e.g. CalendarModal) — don't impose the kit's form-dialog
        // width or grid gap. Kit chrome (radius, shadow, animations) applies.
        className={cn('w-auto max-w-none gap-0 p-4', className)}
      >
        {needsFallbackTitle ? (
          <DialogTitle className="sr-only">{FALLBACK_DIALOG_TITLE}</DialogTitle>
        ) : null}
        <DialogClose asChild>
          <button
            ref={closeBtnRef}
            aria-label="Close"
            className="absolute top-3 right-3 z-[999] grid size-9 cursor-pointer place-items-center border-0 bg-white outline-none [&_svg]:size-[18px]"
          >
            <CloseIcon />
          </button>
        </DialogClose>
        {children}
      </DialogContent>
    </Dialog>
  )
}

export default Modal
